import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

/**
 * Sprint A2 — Assign an onboarding template to a collaborator.
 * Decision 3 (overdue): HR sets `due_at` at assignment time.
 */
export function AssignOnboardingDialog({
  open, onOpenChange, template, collaborators = [], onAssigned,
}) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAssign = async () => {
    if (!userId) {
      toast.error(t('talentstart.assign.errors.userRequired', { defaultValue: 'Pick a collaborator.' }));
      return;
    }
    setSaving(true);
    try {
      const body = { user_id: userId };
      if (dueAt) body.due_at = new Date(dueAt).toISOString();
      const saved = await clientApi.onboardingAssignTemplate(template.id, body);
      toast.success(t('talentstart.assign.success', { defaultValue: 'Onboarding assigned.' }));
      onAssigned?.(saved);
      onOpenChange(false);
      setUserId(''); setDueAt('');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="onboarding-assign-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('talentstart.assign.title', { defaultValue: 'Assign onboarding' })}
            {template && <span className="text-muted-foreground font-normal text-sm"> — {template.name}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('talentstart.assign.user', { defaultValue: 'Collaborator' })}</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="onboarding-assign-user">
                <SelectValue placeholder={t('talentstart.assign.userPlaceholder', { defaultValue: 'Pick a collaborator…' })} />
              </SelectTrigger>
              <SelectContent>
                {collaborators.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} {u.email && <span className="text-muted-foreground text-xs"> · {u.email}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('talentstart.assign.due', { defaultValue: 'Due date (optional)' })}</Label>
            <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                   data-testid="onboarding-assign-due" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleAssign} disabled={saving} data-testid="onboarding-assign-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {t('talentstart.assign.button', { defaultValue: 'Assign' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AssignOnboardingDialog;
